import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import {
  MIN_PASSWORD_LENGTH,
  passwordSchema,
  type InviteLookup,
  type SessionPayload,
} from '@nest-recruit/shared';
import { z } from 'zod';
import { Button } from '../../../components/Button';
import { FloatingField } from '../../../components/FloatingField';
import { SkeletonLines } from '../../../components/Skeleton';
import { useCurrentSession } from '../../../stores/session';
import { useAcceptInvite, useInviteLookup, useLogout } from '../api/auth-api';
import { AuthError, AuthLayout } from '../components/AuthLayout';
import { applyFieldErrors, hasFieldErrors } from './LoginPage';
import styles from './auth-forms.module.css';

/**
 * AUTH-04 — accept an invite and set a password.
 *
 * Accepting signs you straight in: you have just proved you hold the invite, and
 * making somebody type the password they invented four seconds ago is a step that
 * exists only to be annoying.
 *
 * The invite is looked up first (Redesign Group 9): the name the HR Lead typed is
 * prefilled, and a dead link says so before anything is typed.
 */
const acceptFormSchema = z
  .object({
    fullName: z.string().trim().min(2, 'Enter your full name.').max(120),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'Both passwords must match.',
    path: ['confirmPassword'],
  });

type AcceptFormValues = z.infer<typeof acceptFormSchema>;

type DeadInvite = Exclude<InviteLookup['status'], 'valid'>;

const DEAD_INVITE_COPY: Readonly<Record<DeadInvite, { readonly title: string; readonly body: string }>> = {
  expired: {
    title: 'This invite has expired',
    body: 'Invites work for a limited time. Ask your admin to send you a new one.',
  },
  used: {
    title: 'This invite was already used',
    body: 'The account it was for is set up. Sign in with its email and password, or reset the password if you have forgotten it.',
  },
  not_found: {
    title: 'This invite link does not work',
    body: 'It may have been withdrawn or replaced by a newer invite. Ask your admin to send you a new one.',
  },
};

export function AcceptInvitePage(): ReactNode {
  const search = useSearch({ strict: false }) as { token?: string };
  const session = useCurrentSession();

  if (!search.token) {
    return (
      <AuthLayout
        title="This invite link is incomplete"
        tone="danger"
        subtitle="It is missing the code that makes it work."
        footer={
          <Link to="/login" className={styles.link}>
            Back to sign in
          </Link>
        }
      >
        <p className={styles.meta}>
          Open the link from the invite email itself, or ask your admin to send another.
        </p>
      </AuthLayout>
    );
  }

  if (session) return <SignedInVisitor session={session} />;
  return <InviteForm token={search.token} />;
}

/** Somebody already signed in opened an invite link — perhaps a colleague's, on a shared machine. */
function SignedInVisitor({ session }: { readonly session: SessionPayload }): ReactNode {
  // Stays here: once signed out, this same page offers the invite form.
  const logout = useLogout({ isLandingOnSignIn: false });
  return (
    <AuthLayout
      title="You're already signed in"
      tone="warning"
      subtitle={`You're signed in as ${session.fullName}. Sign out to accept this invite.`}
      footer={
        <Link to="/" className={styles.link}>
          Stay signed in and go to Nest Recruit
        </Link>
      }
    >
      <Button variant="solid" size="lg" block isPending={logout.isPending} onClick={() => logout.mutate()}>
        Sign out
      </Button>
    </AuthLayout>
  );
}

function InviteForm({ token }: { readonly token: string }): ReactNode {
  const lookup = useInviteLookup(token);

  // The first paint of data never on screen before: a skeleton, not the form, so the form never
  // appears empty and then fills in under somebody's cursor (CLAUDE.md §5a).
  if (lookup.isPending) {
    return (
      <AuthLayout title="Set up your account" subtitle="Choose a password and you're in. This link works once.">
        <SkeletonLines count={4} />
      </AuthLayout>
    );
  }

  const invite = lookup.data;
  if (invite && invite.status !== 'valid') return <DeadInvitePage status={invite.status} />;

  // A failed lookup still offers the form: accepting reports the real problem if there is one.
  return <SetPasswordForm token={token} fullName={invite?.status === 'valid' ? invite.fullName : ''} />;
}

function DeadInvitePage({ status }: { readonly status: DeadInvite }): ReactNode {
  const copy = DEAD_INVITE_COPY[status];
  return (
    <AuthLayout
      title={copy.title}
      tone="danger"
      footer={
        <Link to="/login" className={styles.link}>
          Back to sign in
        </Link>
      }
    >
      <p className={styles.meta}>{copy.body}</p>
    </AuthLayout>
  );
}

function SetPasswordForm({ token, fullName }: { readonly token: string; readonly fullName: string }): ReactNode {
  const navigate = useNavigate();
  const accept = useAcceptInvite();

  const form = useForm<AcceptFormValues>({
    resolver: zodResolver(acceptFormSchema),
    defaultValues: { fullName, password: '', confirmPassword: '' },
    mode: 'onBlur',
  });

  // With the name already there, the next thing to type is the password.
  useEffect(() => {
    form.setFocus(fullName === '' ? 'fullName' : 'password');
  }, [form, fullName]);

  const onSubmit = form.handleSubmit(async (values) => {
    const payload = await accept
      .mutateAsync({ token, password: values.password, fullName: values.fullName })
      .catch((error: unknown) => {
        applyFieldErrors(error, form.setError);
        return null;
      });
    if (!payload) return;
    // Straight to the welcome screen: a first-time user has no idea what this is
    // yet, and a dashboard is not an introduction.
    await navigate({ to: '/welcome' });
  });

  return (
    <AuthLayout
      title="Set up your account"
      subtitle="Choose a password and you're in. This link works once."
    >
      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        {accept.error && !hasFieldErrors(accept.error) && (
          <AuthError message={accept.error.message} />
        )}

        <FloatingField
          {...form.register('fullName')}
          label="Your full name"
          hint="As you'd like it to appear to colleagues."
          error={form.formState.errors.fullName?.message}
          isRequired
          autoComplete="name"
        />

        <FloatingField
          {...form.register('password')}
          label="Password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          error={form.formState.errors.password?.message}
          isRequired
          type="password"
          autoComplete="new-password"
        />

        <FloatingField
          {...form.register('confirmPassword')}
          label="Type it again"
          error={form.formState.errors.confirmPassword?.message}
          isRequired
          type="password"
          autoComplete="new-password"
        />

        <div className={styles.actions}>
          <Button type="submit" variant="solid" size="lg" block isPending={accept.isPending}>
            Create my account
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
